import { useMemo, useRef, useState } from 'react';
import {
  Alert,
  AttachmentField,
  AttachmentGallery,
  Badge,
  Button,
  NotProvided,
  readAttachmentContent,
  useAttachments,
  type AttachmentItem,
  type AttachmentRestriction,
} from '@evogard/ui';
import {
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_TONES,
  format,
  formatInstant,
  fromColumn,
  type PaymentStatus,
} from '@evogard/domain';
import type {
  AttachmentUrl,
  MyPayment,
  PaymentFile,
  PaymentNote,
  PaymentTransition,
} from '@evogard/contract';
import { api, errorMessage } from '../lib/api.js';

/**
 * As peças do pagamento da premiação que as telas desenham: o cartão do
 * Painel de Gestão (o consultor), o quadro do consultor e o do Financeiro.
 *
 * Moram aqui, e não em nenhuma das duas pastas, porque uma tela de módulo não
 * importa da pasta de outro — e a pílula da fase desenhada duas vezes é a fase
 * com duas cores no dia em que alguém trocar uma.
 */

/** A pílula da fase. O rótulo e o tom vêm de `@evogard/domain`. */
/**
 * A fase do lote. `label` é o nome que a praça deu à fase no quadro de
 * Pagamentos Recorrência (02/10/2026); ausente, é o de nascença. O tom não
 * acompanha a cor da coluna: é o tom do ESTADO, e diz o mesmo em toda tela.
 */
export function PaymentStatusBadge({
  status,
  label,
}: {
  status: PaymentStatus;
  label?: string;
}) {
  return (
    <Badge tone={PAYMENT_STATUS_TONES[status]}>
      {label ?? PAYMENT_STATUS_LABELS[status]}
    </Badge>
  );
}

/**
 * As notas e o comprovante, do mais novo ao mais velho, na grade de anexos da
 * casa (`AttachmentGallery`): miniatura e, no clique, o visualizador por cima
 * da tela — sem abrir outra aba.
 *
 * `sign` é a rota de quem olha: a do consultor escopa pela pessoa, a do
 * Financeiro pela praça. A lista não sabe qual é, e não precisa. A URL é
 * assinada e vence, então vira `resolveUrl` — pedida por quem desenha, nunca
 * guardada.
 *
 * `sign` mora numa ref porque as duas telas a passam como arrow inline: uma
 * `resolveUrl` nova a cada render refaz o efeito de `useSource`, que pede a URL,
 * que re-renderiza — um laço de requisições.
 */
export function PaymentFileList({
  files,
  sign,
}: {
  files: readonly PaymentFile[];
  sign: (fileId: number) => Promise<AttachmentUrl>;
}) {
  const signRef = useRef(sign);
  signRef.current = sign;

  const items = useMemo<AttachmentItem[]>(
    () =>
      files.map((f) => ({
        key: String(f.id),
        nome: f.nome,
        typeMime: f.typeMime,
        sizeBytes: f.sizeBytes,
        detail: [
          f.kind === 'comprovante' ? 'Comprovante' : 'Nota fiscal',
          formatInstant(f.createdAt),
          f.createdBy,
        ]
          .filter(Boolean)
          .join(' · '),
        resolveUrl: async () => (await signRef.current(f.id)).url,
      })),
    [files],
  );

  return <AttachmentGallery items={items} />;
}

/**
 * As observações do cartão, da mais velha à mais nova — o que o Financeiro
 * escreveu (e, nas linhas de antes de 24/09/2026, o que ele corrigiu na nota).
 * O consultor lê as mesmas linhas: não há observação interna (ver
 * `management_payment_notes`).
 */
export function PaymentNotes({ notes }: { notes: readonly PaymentNote[] }) {
  if (notes.length === 0) return <NotProvided>Nenhuma observação ainda.</NotProvided>;
  return (
    <ol className="flex flex-col gap-2 text-sm">
      {notes.map((n) => (
        <li key={n.id} className="flex flex-col">
          <span className="text-texto-suave">
            {n.kind === 'edicao' ? 'Edição da nota' : 'Observação'} ·{' '}
            {formatInstant(n.at)} · {n.by ?? 'sistema'}
          </span>
          <span className="whitespace-pre-line">{n.body}</span>
        </li>
      ))}
    </ol>
  );
}

/** A linha do tempo das fases, com quem moveu e o motivo. */
export function PaymentHistory({
  transitions,
}: {
  transitions: readonly PaymentTransition[];
}) {
  return (
    <ol className="flex flex-col gap-2 text-sm">
      {transitions.map((t, i) => (
        <li key={i} className="flex flex-col">
          <span>
            <strong>{PAYMENT_STATUS_LABELS[t.to]}</strong>
            <span className="text-texto-suave">
              {' '}
              · {formatInstant(t.at)} · {t.by ?? 'sistema'}
            </span>
          </span>
          {t.note && <span className="text-texto-suave">{t.note}</span>}
        </li>
      ))}
    </ol>
  );
}

/** A nota do lote no Início: só PDF, até 10 MB — a referência do dono (28/10/2026). */
const INVOICE_PDF: AttachmentRestriction = {
  accept: ['application/pdf'],
  sentence: 'Só PDF, até 10 MB.',
  check: (typeMime, sizeBytes) =>
    typeMime !== 'application/pdf'
      ? 'A nota fiscal vai em PDF.'
      : sizeBytes > 10 * 1024 * 1024
        ? 'O arquivo passa de 10 MB.'
        : null,
};

/**
 * O envio (ou reenvio) da nota pelo PRÓPRIO consultor — no Painel de Gestão e
 * no cartão do quadro dele. Quem decide se o formulário aparece é quem o
 * desenha, com o `sendRefusal` que veio do servidor; aqui só se envia.
 *
 * **Só o arquivo** (24/09/2026): o valor é o apurado, e o primeiro envio faz
 * nascer a despesa do Financeiro. Não há número nem valor a digitar — o
 * Financeiro confere abrindo a nota.
 */
export function InvoiceForm({
  payment: p,
  onSent,
  variant = 'padrao',
}: {
  payment: Pick<MyPayment, 'id' | 'status' | 'amount'>;
  onSent: (sent: MyPayment) => void;
  /**
   * `lote`: o desenho do envio no Início do Comercial (28/10/2026) — sem a
   * frase do valor (o modal já o mostra em destaque), só PDF de até 10 MB, a
   * área inteira clicável e o botão na largura toda.
   */
  variant?: 'padrao' | 'lote';
}) {
  const lot = variant === 'lote';
  const file = useAttachments();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready = file.items.length === 1;

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const chosen = file.items[0]!;
      const r = await api.management.sendInvoice({
        params: { id: p.id },
        body: {
          file: {
            nome: chosen.nome,
            typeMime: chosen.typeMime,
            content: await readAttachmentContent(chosen.file),
          },
        },
      });
      if (r.status !== 201) throw new Error(errorMessage(r.body));
      onSent(r.body);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Algo deu errado.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) void send();
      }}
    >
      {!lot && (
        <p className="text-sm text-texto-suave">
          A nota deve ser emitida no valor apurado, de {format(fromColumn(p.amount)!)}.
        </p>
      )}
      <AttachmentField
        label={
          lot ? (
            <span className="rotulo text-texto-suave">
              Anexar nota fiscal (PDF) <span className="text-erro">*</span>
            </span>
          ) : (
            'Arquivo da nota'
          )
        }
        items={file.items}
        onAdd={file.add}
        onRemove={file.remove}
        max={1}
        disabled={busy}
        {...(lot && {
          restrict: INVOICE_PDF,
          prompt: { title: 'Clique ou arraste o PDF', hint: 'Máx. 10 MB' },
        })}
      />
      {error && <Alert tone="erro">{error}</Alert>}
      <Button
        type="submit"
        icon="documento"
        loading={busy}
        disabled={!ready}
        className={lot ? 'w-full' : undefined}
      >
        {lot
          ? 'Enviar documento'
          : p.status === 'pendente'
            ? 'Reenviar a nota'
            : 'Enviar a nota'}
      </Button>
    </form>
  );
}
